/**
 * Snipcart JavaScript SDK Utility
 *
 * This module provides a wrapper around the Snipcart JavaScript SDK API,
 * offering methods to interact with the cart items service.
 *
 * All async methods return promises and throw an SDKError in case of failure.
 */

/**
 * Custom error class for Snipcart SDK errors
 */
export class SDKError extends Error {
	constructor(message, originalError = null) {
		super(message);
		this.name = 'SDKError';
		this.originalError = originalError;
	}
}

/**
 * Checks if Snipcart SDK is available
 * @throws {SDKError} If Snipcart is not loaded
 */
const ensureSnipcart = () => {
	if (typeof Snipcart === 'undefined' || !Snipcart.api) {
		throw new SDKError('Snipcart SDK is not loaded. Ensure the Snipcart script is included in your HTML.');
	}
};

// ============================================================================
// ITEMS OPERATIONS
// ============================================================================

/**
 * Items service for managing cart items
 */
export const items = {
	/**
	 * Adds one or more items to the cart
	 *
	 * @param {...Object} productDefinitions - Product definition(s) to add
	 * @param {string} productDefinitions.id - Product ID
	 * @param {string} productDefinitions.name - Product name
	 * @param {number} productDefinitions.price - Product price
	 * @param {string} productDefinitions.url - Product URL
	 * @param {number} [productDefinitions.quantity=1] - Quantity to add
	 * @param {Object} [productDefinitions.alternatePrices] - Alternate price lists
	 * @param {Object} [productDefinitions.customFields] - Custom fields
	 * @returns {Promise<Object>} CartItemAddedResponse
	 * @throws {SDKError} If adding items fails
	 *
	 * @example
	 * // Add single item
	 * await items.add({
	 *   id: 'PRODUCT_ID',
	 *   name: 'Product 1',
	 *   price: 1.11,
	 *   url: '/',
	 *   quantity: 1
	 * });
	 *
	 * @example
	 * // Add multiple items
	 * await items.add(
	 *   { id: 'PRODUCT1', name: 'Product 1', price: 10, url: '/', quantity: 1 },
	 *   { id: 'PRODUCT2', name: 'Product 2', price: 20, url: '/', quantity: 2 }
	 * );
	 *
	 * @example
	 * // Add item with alternate prices
	 * await items.add({
	 *   id: 'PRODUCT_ID',
	 *   name: 'Product 1',
	 *   price: 20.00,
	 *   alternatePrices: { vip: 10.00 },
	 *   url: '/',
	 *   quantity: 1
	 * });
	 */
	async add(...productDefinitions) {
		try {
			ensureSnipcart();
			return await Snipcart.api.cart.items.add(...productDefinitions);
		} catch (error) {
			throw new SDKError('Failed to add items to cart', error);
		}
	},
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Helper function to create a product definition object
 *
 * @param {Object} options - Product options
 * @param {string} options.id - Product ID
 * @param {string} options.name - Product name
 * @param {number} options.price - Product price
 * @param {string} options.url - Product URL
 * @param {string} [options.description=''] - Product description
 * @param {number} [options.quantity=1] - Quantity
 * @param {Object} [options.alternatePrices] - Alternate price lists
 * @param {Object} [options.customFields] - Custom fields
 * @param {Object} [options.metadata] - Metadata key-value pairs attached to the item
 * @returns {Object} Product definition object
 *
 * @example
 * const product = createProductDefinition({
 *   id: 'TOUR-001',
 *   name: 'City Tour',
 *   price: 25.00,
 *   url: '/tours/city-tour',
 *   quantity: 2
 * });
 * await items.add(product);
 */
export const createProductDefinition = ({
	id,
	name,
	price,
	url,
	description = '',
	quantity = 1,
	alternatePrices = null,
	customFields = null,
	metadata = null,
}) => {
	const product = {
		id,
		name,
		price,
		url,
		quantity,
	};

	if (description) {
		product.description = description;
	}

	if (alternatePrices) {
		product.alternatePrices = alternatePrices;
	}

	if (customFields) {
		product.customFields = customFields;
	}

	if (metadata) {
		product.metadata = metadata;
	}

	return product;
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
	items,
	createProductDefinition,
	SDKError,
};
